<template>
  <div class="delete-certificate-modal">
    <rbz-modal-dialog
      :show="showDeleteModal"
      :title="modalTitle"
      :subtitle="selectedCertificate?.name"
      :primary-btn-title="t('common.operations.delete.label')"
      @onPrimaryBtnClick="emit('delete-certificate')"
      :secondary-btn-title="t('common.operations.cancel.label')"
      @onSecondaryBtnClick="closeDeleteModal"
      @closeModal="closeDeleteModal"
      size="medium"
    >
      <template #content>
        <div>
          {{ t('certificate.warningConfirm') }}
        </div>
        <div v-if="attachedApps.length" class="attached-apps">
          {{ t('certificate.warningAttached') }}
          <div>
            <div
              v-for="app in attachedApps"
              :key="app"
              :ref="(el) => (attachedAppsRefs[app] = el)"
              v-b-tooltip="Utils.elementTreeOverflow(attachedAppsRefs[app]) ? app : ''"
              class="attached-app ellipsis"
            >
              • {{ app }}
            </div>
          </div>
        </div>
      </template>
    </rbz-modal-dialog>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import RbzModalDialog from '@/components/RbzModalDialog.vue';
import Utils from 'ui-component-library/helpers/Utils';
import { Certificate } from '@/types';
import RequestsUtils from '@/helpers/RequestsUtils';
import { useBranchesStore } from '@/store/BranchesStore';

interface CertificatesListDeleteModalProps {
  attachedApps: string[];
  entityName: { [key: string]: string };
  side: Certificate['side'];
  selectedCertificate: Certificate;
}

const props = defineProps<CertificatesListDeleteModalProps>();

interface CertificatesListDeleteModalEmits {
  (e: 'delete-certificate'): void;
}

const emit = defineEmits<CertificatesListDeleteModalEmits>();
const branchesStore = useBranchesStore();
const { t } = useI18n();
const showDeleteModal = ref(false);
const attachedAppsRefs = ref<{ [key: string]: HTMLDivElement }>({});

const modalTitle = computed(() => {
  let certificateSide = '';
  switch (props.side) {
    case 'server': {
      certificateSide = t('certificate.serverCertificate.singular');
      break;
    }
    case 'clientCA': {
      certificateSide = t('certificate.clientCaCertificate.singular');
      break;
    }
    case 'serverToBackendMTLS': {
      certificateSide = t('certificate.serverToBackendMtlsCertificate.singular');
      break;
    }
    case 'backendCA': {
      certificateSide = t('certificate.serverToBackendCaCertificate.singular');
      break;
    }
  }
  return t('common.operations.delete.modal.title', { singular: certificateSide });
});

function closeDeleteModal() {
  showDeleteModal.value = false;
}

function openDeleteModal() {
  showDeleteModal.value = true;
}

async function deleteCertificate() {
  const successMessage = t('common.operations.delete.messages.success', props.entityName);
  const failureMessage = t('common.operations.delete.messages.failure', props.entityName);
  await RequestsUtils.sendRequest({
    methodName: 'DELETE',
    url: `conf/${branchesStore.selectedBranchId}/certificates/${props.selectedCertificate.id}`,
    successMessage,
    failureMessage,
  });
  closeDeleteModal();
}

defineExpose({
  openDeleteModal,
  deleteCertificate,
});
</script>

<style scoped lang="scss">
.attached-apps {
  margin-top: 16px;
}
</style>
